import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { Dayjs } from 'dayjs';

type CouponsFilterValues = {
  couponNameValue: string;
  promoCodeValue: string;
  discountValue: string;
  statusValues: string[];
  rangeDates: [Dayjs, Dayjs] | undefined;
};

export const hasAppliedCouponsFilters = ({
  couponNameValue,
  promoCodeValue,
  discountValue,
  statusValues,
  rangeDates
}: CouponsFilterValues) =>
  couponNameValue !== '' ||
  promoCodeValue !== '' ||
  discountValue !== '' ||
  statusValues.length > 0 ||
  rangeDates !== undefined;

export const useCouponsFilters = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isSyncingToUrl = useRef(false);
  const isSyncingFromUrl = useRef(false);

  const [couponNameValue, setCouponNameValue] = useState<string>(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('couponName') || '';
  });

  const [promoCodeValue, setPromoCodeValue] = useState<string>(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('promoCode') || '';
  });

  const [discountValue, setDiscountValue] = useState<string>(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('discount') || '';
  });

  const [statusValues, setStatusValues] = useState<string[]>(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');
    return statusParam ? statusParam.split(',').filter(Boolean) : [];
  });

  const [rangeDates, setRangeDates] = useState<[Dayjs, Dayjs] | undefined>(
    undefined
  );
  const [formattedRangeDates, setFormattedRangeDates] = useState<
    string[] | null[]
  >([null, null]);
  const [, setPreviousFormattedRangeDates] = useState<string[] | null[]>([
    null,
    null
  ]);

  const prevLocationSearch = useRef(location.search);

  useEffect(() => {
    if (isSyncingToUrl.current) {
      isSyncingToUrl.current = false;
      prevLocationSearch.current = location.search;
      return;
    }

    if (prevLocationSearch.current === location.search) {
      return;
    }
    prevLocationSearch.current = location.search;

    isSyncingFromUrl.current = true;

    const searchParams = new URLSearchParams(location.search);

    const couponNameParam = searchParams.get('couponName') || '';
    const promoCodeParam = searchParams.get('promoCode') || '';
    const discountParam = searchParams.get('discount') || '';
    const statusParam = searchParams.get('status');
    const newStatusValues = statusParam
      ? statusParam.split(',').filter(Boolean)
      : [];

    setCouponNameValue(couponNameParam);
    setPromoCodeValue(promoCodeParam);
    setDiscountValue(discountParam);
    setStatusValues(newStatusValues);

    setTimeout(() => {
      isSyncingFromUrl.current = false;
    }, 0);
  }, [location.search]);

  useEffect(() => {
    if (isSyncingFromUrl.current) {
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    let hasChanges = false;

    const currentCouponNameParam = queryParams.get('couponName') || '';
    if (currentCouponNameParam !== couponNameValue) {
      if (couponNameValue) {
        queryParams.set('couponName', couponNameValue);
      } else {
        queryParams.delete('couponName');
      }
      hasChanges = true;
    }

    const currentPromoCodeParam = queryParams.get('promoCode') || '';
    if (currentPromoCodeParam !== promoCodeValue) {
      if (promoCodeValue) {
        queryParams.set('promoCode', promoCodeValue);
      } else {
        queryParams.delete('promoCode');
      }
      hasChanges = true;
    }

    const currentDiscountParam = queryParams.get('discount') || '';
    if (currentDiscountParam !== discountValue) {
      if (discountValue) {
        queryParams.set('discount', discountValue);
      } else {
        queryParams.delete('discount');
      }
      hasChanges = true;
    }

    const currentStatusParam = queryParams.get('status') || '';
    const newStatusValue = statusValues.join(',');
    if (currentStatusParam !== newStatusValue) {
      if (statusValues.length > 0) {
        queryParams.set('status', newStatusValue);
      } else {
        queryParams.delete('status');
      }
      hasChanges = true;
    }

    if (hasChanges) {
      isSyncingToUrl.current = true;
      const queryString = queryParams.toString();
      const newUrl = queryString
        ? `${location.pathname}?${queryString}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [
    couponNameValue,
    promoCodeValue,
    discountValue,
    statusValues,
    location.pathname,
    navigate
  ]);

  const handleCouponNameChange = useCallback((value: string) => {
    setCouponNameValue(value);
  }, []);

  const handlePromoCodeChange = useCallback((value: string) => {
    setPromoCodeValue(value);
  }, []);

  const handleDiscountChange = useCallback((value: string) => {
    setDiscountValue(value);
  }, []);

  const handleStatusChange = useCallback((ids: string[]) => {
    setStatusValues(ids);
  }, []);

  const hasAppliedFilters = useMemo(
    () =>
      hasAppliedCouponsFilters({
        couponNameValue,
        promoCodeValue,
        discountValue,
        statusValues,
        rangeDates
      }),
    [couponNameValue, promoCodeValue, discountValue, statusValues, rangeDates]
  );

  const handleClearFilters = useCallback(() => {
    setCouponNameValue('');
    setPromoCodeValue('');
    setDiscountValue('');
    setStatusValues([]);
    setRangeDates(undefined);
    setFormattedRangeDates([null, null]);
  }, []);

  return {
    couponNameValue,
    handleCouponNameChange,
    promoCodeValue,
    handlePromoCodeChange,
    discountValue,
    handleDiscountChange,
    setDiscountValue,
    statusValues,
    handleStatusChange,
    rangeDates,
    setRangeDates,
    formattedRangeDates,
    setFormattedRangeDates,
    setPreviousFormattedRangeDates,
    hasAppliedFilters,
    handleClearFilters
  };
};
